import { useMemo, type ReactNode } from "react";

const QR_VERSION = 5;
const QR_SIZE = 17 + QR_VERSION * 4;
const DATA_CODEWORDS = 108;
const ERROR_CODEWORDS = 26;
const QUIET_ZONE = 4;

type Matrix = Array<Array<boolean | undefined>>;

function appendBits(bits: boolean[], value: number, length: number): void {
  for (let bit = length - 1; bit >= 0; bit -= 1) bits.push(((value >>> bit) & 1) === 1);
}

function makeGaloisTables(): { exp: number[]; log: number[] } {
  const exp = Array<number>(512).fill(0);
  const log = Array<number>(256).fill(0);
  let value = 1;
  for (let index = 0; index < 255; index += 1) {
    exp[index] = value;
    log[value] = index;
    value <<= 1;
    if ((value & 0x100) !== 0) value ^= 0x11d;
  }
  for (let index = 255; index < 512; index += 1) exp[index] = exp[index - 255] ?? 0;
  return { exp, log };
}

const GALOIS = makeGaloisTables();

function multiply(left: number, right: number): number {
  if (left === 0 || right === 0) return 0;
  return GALOIS.exp[(GALOIS.log[left] ?? 0) + (GALOIS.log[right] ?? 0)] ?? 0;
}

function errorCorrection(data: number[]): number[] {
  let generator = [1];
  for (let degree = 0; degree < ERROR_CODEWORDS; degree += 1) {
    const next = Array<number>(generator.length + 1).fill(0);
    generator.forEach((coefficient, index) => {
      next[index] = (next[index] ?? 0) ^ coefficient;
      next[index + 1] = (next[index + 1] ?? 0) ^ multiply(coefficient, GALOIS.exp[degree] ?? 0);
    });
    generator = next;
  }

  const remainder = Array<number>(ERROR_CODEWORDS).fill(0);
  data.forEach((codeword) => {
    const factor = codeword ^ (remainder[0] ?? 0);
    remainder.shift();
    remainder.push(0);
    for (let index = 0; index < ERROR_CODEWORDS; index += 1) {
      remainder[index] = (remainder[index] ?? 0) ^ multiply(generator[index + 1] ?? 0, factor);
    }
  });
  return remainder;
}

function finder(matrix: Matrix, left: number, top: number): void {
  for (let y = -1; y <= 7; y += 1) {
    for (let x = -1; x <= 7; x += 1) {
      const targetX = left + x;
      const targetY = top + y;
      if (targetX < 0 || targetY < 0 || targetX >= QR_SIZE || targetY >= QR_SIZE) continue;
      const dark =
        x >= 0 &&
        x <= 6 &&
        y >= 0 &&
        y <= 6 &&
        (x === 0 || x === 6 || y === 0 || y === 6 || (x >= 2 && x <= 4 && y >= 2 && y <= 4));
      matrix[targetY]![targetX] = dark;
    }
  }
}

function alignment(matrix: Matrix, centerX: number, centerY: number): void {
  for (let y = -2; y <= 2; y += 1) {
    for (let x = -2; x <= 2; x += 1) {
      matrix[centerY + y]![centerX + x] = Math.max(Math.abs(x), Math.abs(y)) !== 1;
    }
  }
}

function formatBits(mask: number): number {
  const data = (1 << 3) | mask; // Error correction level L.
  let value = data << 10;
  while (Math.floor(Math.log2(value)) >= 10) {
    value ^= 0x537 << (Math.floor(Math.log2(value)) - 10);
  }
  return ((data << 10) | value) ^ 0x5412;
}

function reserveAndWriteFormat(matrix: Matrix, mask: number): void {
  const bits = formatBits(mask);
  for (let index = 0; index < 15; index += 1) {
    const dark = ((bits >>> index) & 1) === 1;
    const verticalY = index < 6 ? index : index < 8 ? index + 1 : QR_SIZE - 15 + index;
    const horizontalX = index < 8 ? QR_SIZE - index - 1 : index === 8 ? 7 : 15 - index - 1;
    matrix[verticalY]![8] = dark;
    matrix[8]![horizontalX] = dark;
  }
  matrix[QR_SIZE - 8]![8] = true;
}

function createQrMatrix(value: string): boolean[][] | undefined {
  const bytes = [...new TextEncoder().encode(value)];
  if (bytes.length > 106) return undefined;

  const bits: boolean[] = [];
  appendBits(bits, 0b0100, 4);
  appendBits(bits, bytes.length, 8);
  bytes.forEach((byte) => appendBits(bits, byte, 8));
  const capacity = DATA_CODEWORDS * 8;
  appendBits(bits, 0, Math.min(4, capacity - bits.length));
  while (bits.length % 8 !== 0) bits.push(false);

  const data: number[] = [];
  for (let offset = 0; offset < bits.length; offset += 8) {
    let codeword = 0;
    for (let bit = 0; bit < 8; bit += 1) codeword = (codeword << 1) | (bits[offset + bit] ? 1 : 0);
    data.push(codeword);
  }
  while (data.length < DATA_CODEWORDS) data.push(data.length % 2 === 0 ? 0xec : 0x11);

  const codewords = [...data, ...errorCorrection(data)];
  const dataBits: boolean[] = [];
  codewords.forEach((codeword) => appendBits(dataBits, codeword, 8));

  const matrix: Matrix = Array.from({ length: QR_SIZE }, () => Array<boolean | undefined>(QR_SIZE));
  finder(matrix, 0, 0);
  finder(matrix, QR_SIZE - 7, 0);
  finder(matrix, 0, QR_SIZE - 7);
  alignment(matrix, 30, 30);
  for (let index = 8; index < QR_SIZE - 8; index += 1) {
    if (matrix[6]![index] === undefined) matrix[6]![index] = index % 2 === 0;
    if (matrix[index]![6] === undefined) matrix[index]![6] = index % 2 === 0;
  }
  reserveAndWriteFormat(matrix, 0);

  let bitIndex = 0;
  let upwards = true;
  for (let right = QR_SIZE - 1; right > 0; right -= 2) {
    if (right === 6) right -= 1;
    for (let vertical = 0; vertical < QR_SIZE; vertical += 1) {
      const y = upwards ? QR_SIZE - 1 - vertical : vertical;
      for (let offset = 0; offset < 2; offset += 1) {
        const x = right - offset;
        if (matrix[y]![x] !== undefined) continue;
        const source = dataBits[bitIndex] ?? false;
        matrix[y]![x] = source !== ((x + y) % 2 === 0);
        bitIndex += 1;
      }
    }
    upwards = !upwards;
  }

  return matrix.map((row) => row.map((module) => module ?? false));
}

export function QrPresentation({
  value,
  label = "QR code for the TV display link",
}: {
  value: string;
  label?: string;
}): ReactNode {
  const matrix = useMemo(() => createQrMatrix(value), [value]);
  if (!matrix)
    return (
      <p className="lobby-help">Display URL is too long for the local QR code. Copy it instead.</p>
    );

  const size = QR_SIZE + QUIET_ZONE * 2;
  return (
    <svg
      className="display-qr"
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={label}
      shapeRendering="crispEdges"
    >
      <title>{label}</title>
      <rect width={size} height={size} fill="#fff" />
      {matrix.flatMap((row, y) =>
        row.map((dark, x) =>
          dark ? (
            <rect
              key={`${x}-${y}`}
              x={x + QUIET_ZONE}
              y={y + QUIET_ZONE}
              width="1"
              height="1"
              fill="#000"
            />
          ) : null,
        ),
      )}
    </svg>
  );
}
